import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle.js';
import { Button } from '@/components/ui/button';
import { useCart } from '@/contexts/CartContext';
import { useToast } from '@/components/ui/use-toast';

export default function ThankYou() {
  const { payload, clearCart, resetOrderSession } = useCart();
  const location = useLocation();
  const bitToastRef = useRef(null);
  const clearedRef = useRef(false);
  const paymentMethodRef = useRef(
    String(payload?.paymentMethod || payload?.financial?.payment_method || '').toLowerCase(),
  );
  const idempotencyKeyRef = useRef(String(payload?.idempotency_key || ''));
  const capturedPaymentContextRef = useRef(location.state?.capturedPaymentContext || null);
  const { toast } = useToast();

  // This route is order acknowledgement only. BIT, wire, cheque, and failed-card
  // fallbacks must never emit a Purchase event or mark an Airtable record paid.
  useEffect(() => {
    if (clearedRef.current) return;
    clearedRef.current = true;
    try { localStorage.removeItem('order_payload_for_gtag'); } catch (_) {}
    try { clearCart(); } catch (_) {}
    try { resetOrderSession(); } catch (_) {}
  }, [clearCart, resetOrderSession]);

  const paymentMethod = paymentMethodRef.current;
  const capturedPaymentContext = capturedPaymentContextRef.current;
  const isBitPayment = (
    paymentMethod === 'bit'
    && capturedPaymentContext?.paymentMethod === 'bit'
    && Boolean(idempotencyKeyRef.current)
    && capturedPaymentContext?.idempotencyKey === idempotencyKeyRef.current
  );

  useEffect(() => {
    if (!isBitPayment) {
      if (bitToastRef.current) {
        try { bitToastRef.current.dismiss?.(); } catch (_) {}
        bitToastRef.current = null;
      }
      return undefined;
    }
    const nextToast = toast({
      variant: 'payment',
      title: <span className="block text-right">Bit / Paybox</span>,
      description: (
        <div className="space-y-3 text-right">
          <p>
            ניתן להעביר את התשלום בביט או פייבוקס למספר הטלפון:
          </p>
          <a
            className="block text-3xl font-black tracking-tight text-slate-900 underline decoration-2 underline-offset-4"
            dir="ltr"
            href="tel:0546969974"
          >
            054-696-9974
          </a>
          <p>
            לאחר קליטת התשלום ההזמנה תיכנס לעבודה באופן אוטומטי.
          </p>
        </div>
      ),
      duration: Infinity,
    });
    bitToastRef.current = nextToast;
    return () => {
      try { nextToast?.dismiss?.(); } catch (_) {}
      bitToastRef.current = null;
    };
  }, [isBitPayment, toast]);

  return (
    <>
      <div className="min-h-screen py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <CheckCircle className="h-24 w-24 text-green-500 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-gray-900 mb-4">תודה רבה!</h1>
            <p className="text-lg text-gray-600 mb-6">ההזמנה נקלטה בהצלחה. נשלח אליך אישור במייל בקרוב והצוות שלנו ייצור איתך קשר להמשך התהליך.</p>

            {/* Summary removed per request */}

            <div className="flex items-center justify-center gap-3 mb-6">
              <Link to="/catalog"><Button>המשך לקניות</Button></Link>
            </div>

          </motion.div>
        </div>
      </div>
    </>
  );
}
